import React, { useCallback, useEffect, useRef, useState } from "react";
import officeImageUrl from "./assets/office_image.png";

const PUBLIC_ASSET = (name) => {
  const base = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
  return `${base}/${name}`;
};

const LOGIN_GRADIENT =
  "linear-gradient(155deg, rgba(0,12,32,0.55) 0%, rgba(0,18,45,0.50) 60%, rgba(0,10,28,0.58) 100%)";

function LoginBackdrop({ children }) {
  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <img
        src={officeImageUrl}
        alt=""
        aria-hidden="true"
        decoding="async"
        fetchPriority="high"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          zIndex: 0,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: LOGIN_GRADIENT,
          zIndex: 1,
        }}
      />
      {children}
    </div>
  );
}

/** Login look (refactored 2026-09-05 to the app's square, compact style — auth logic untouched). */
const L = {
  navy: "#000613",
  text: "#0f172a",
  mid: "#475569",
  light: "#94a3b8",
  border: "#d8dee8",
  bg: "#f8fafc",
  card: "#ffffff",
  danger: "#b91c1c",
  amberBg: "#FEF3C7",
  amberBorder: "#F59E0B",
  amberText: "#7c4a00",
};
const BTN_STYLE = {
  height: 36,
  borderRadius: 4,
  border: `1px solid ${L.navy}`,
  background: L.navy,
  color: "#fff",
  cursor: "pointer",
  fontWeight: 800,
  fontSize: 13,
  width: "100%",
  padding: "0 14px",
  whiteSpace: "nowrap",
};
const LOGIN_CARD = {
  position: "relative",
  zIndex: 10,
  background: L.card,
  border: `1px solid ${L.border}`,
  borderRadius: 4,
  padding: "22px 24px 18px",
  width: "100%",
  maxWidth: 440,
  boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
  fontFamily: "Inter, system-ui, sans-serif",
  color: L.text,
};
const LOGIN_CAPTION = { fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, color: L.light };
const LOGIN_INPUT = { height: 36, boxSizing: "border-box", borderRadius: 4, border: `1px solid ${L.border}`, padding: "0 10px", fontSize: 14, color: L.text, background: L.card, outline: "none", width: "100%" };

/** OAuth Web Client ID is public; env override for other environments / CI builds that omit .env.production */
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "546030892226-gmarsdas9hs9igr332g94od7u0s82amu.apps.googleusercontent.com";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/** Fallbacks match backend server.js SESSION_IDLE_MS (15m) and 5-minute warning lead. */
const DEFAULT_SESSION_IDLE_MS = 15 * 60 * 1000;
const DEFAULT_WARNING_LEAD_MS = 5 * 60 * 1000;

function apiUrl(path) {
  if (!path.startsWith("/")) return `${API_BASE}/${path}`;
  return `${API_BASE}${path}`;
}

function authApiTargetHint() {
  const local =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  if (!local) {
    return "Use your Firebase Hosting URL (rewrites /auth and /api to Cloud Run), not a plain static file server.";
  }
  if (API_BASE) {
    return `Frontend is calling ${API_BASE} directly. For local dev, remove REACT_APP_API_BASE_URL from .env.development / .env.local and add http://localhost:3000 to Cloud Run FRONTEND_ORIGINS, or run the API locally.`;
  }
  return "Start the API in a second terminal: cd jf-billing/backend && npm start (port 4000). Keep npm start running for the React app.";
}

async function parseJsonFromApiResponse(res) {
  const text = await res.text();
  if (/^\s*<!doctype/i.test(text) || /^\s*<html/i.test(text)) {
    throw new Error(`Received HTML instead of API JSON. ${authApiTargetHint()}`);
  }
  if (/proxy error|ECONNREFUSED|Could not proxy/i.test(text)) {
    throw new Error(`Cannot reach the authentication API. ${authApiTargetHint()}`);
  }
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    const snippet = text.replace(/\s+/g, " ").trim().slice(0, 120);
    throw new Error(
      snippet
        ? `Invalid JSON from authentication API (HTTP ${res.status}): ${snippet}. ${authApiTargetHint()}`
        : `Invalid JSON from authentication API (HTTP ${res.status}). ${authApiTargetHint()}`
    );
  }
}

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Google Sign-In script failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Google Sign-In script failed"));
    document.body.appendChild(s);
  });
}

/** Text-only differences between the staff app and the client portal (2026-09-06). Not auth logic. */
const AUTH_COPY = {
  main: {
    subtitle: "Medical Billing Portal",
    loadingCaption: "Medical billing portal",
    caption: "Secure sign-in",
    blurb: "Sign in with your J & F Google account.",
    crossLabel: "Client? To portals ↗",
    crossHref: "https://portals.jfprofessionalsolutions.net/",
  },
  portal: {
    subtitle: "Client Portal",
    loadingCaption: "Client portal",
    caption: "Client sign-in",
    blurb: "Sign in with the Google account J & F approved for your company.",
    crossLabel: "Staff? To main login ↗",
    crossHref: "https://jfprofessionalsolutions.net/",
  },
};

export default function AuthGate({ children, variant = "main" }) {
  const copy = AUTH_COPY[variant] || AUTH_COPY.main;
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState("");
  const [csrfToken, setCsrfToken] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaSetupSecret, setMfaSetupSecret] = useState("");
  const [mfaSetupQr, setMfaSetupQr] = useState("");
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [mfaBusy, setMfaBusy] = useState(false);
  const googleBtnRef = useRef(null);
  const [sessionIdleMs, setSessionIdleMs] = useState(DEFAULT_SESSION_IDLE_MS);
  const [sessionWarningLeadMs, setSessionWarningLeadMs] = useState(DEFAULT_WARNING_LEAD_MS);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [idleRemainingSec, setIdleRemainingSec] = useState(300);
  const [phiAcknowledged, setPhiAcknowledged] = useState(false);
  const idleWarnTimerRef = useRef(null);
  const idleCountdownTimerRef = useRef(null);
  const idleCountdownDeadlineRef = useRef(null);
  const idleWarnFireAtRef = useRef(null); // absolute ms timestamp when warning should fire
  const idleWarningOpenRef = useRef(false);
  const onLogoutRef = useRef(async () => {});

  const applyAuthSessionPayload = useCallback((data) => {
    if (!data || typeof data !== "object") return;
    if (typeof data.sessionIdleMs === "number" && data.sessionIdleMs >= 60000) {
      setSessionIdleMs(data.sessionIdleMs);
    }
    if (typeof data.sessionWarningLeadMs === "number" && data.sessionWarningLeadMs >= 10000) {
      setSessionWarningLeadMs(data.sessionWarningLeadMs);
    }
  }, []);

  const handleGoogleCredential = useCallback(async (response) => {
    try {
      setError("");
      const res = await fetch(apiUrl("/auth/google"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await parseJsonFromApiResponse(res);
      if (!res.ok || data.ok !== true || !data.username) {
        throw new Error(data.error || "Google sign-in failed.");
      }
      setCurrentUser(data.username);
      window.__currentUsername = data.username;
      setCsrfToken(data.csrfToken || ""); window.__csrfToken = data.csrfToken || "";
      window.__csrfToken = data.csrfToken || "";
      setMfaEnrolled(Boolean(data.mfaEnrolled));
      applyAuthSessionPayload(data);
      if (data.mfaRequired && !data.mfaVerified) {
        setStatus("mfa_required");
      } else {
        setStatus("authenticated");
      }
    } catch (err) {
      setError(err.message || "Google sign-in failed.");
      setStatus("unauthenticated");
    }
  }, [applyAuthSessionPayload]);

  useEffect(() => {
    let mounted = true;
    async function checkSession() {
      try {
        const res = await fetch(apiUrl("/auth/session"), { credentials: "include" });
        const data = await parseJsonFromApiResponse(res);
        if (!mounted) return;
        if (res.ok && data.ok === true && data.authenticated && data.username) {
          setCurrentUser(data.username);
          window.__currentUsername = data.username;
          setCsrfToken(data.csrfToken || ""); window.__csrfToken = data.csrfToken || "";
          setMfaEnrolled(Boolean(data.mfaEnrolled));
          applyAuthSessionPayload(data);
          setStatus("authenticated");
          return;
        }
        if (res.ok && data.ok === true && data.username && data.mfaRequired && !data.mfaVerified) {
          setCurrentUser(data.username);
          window.__currentUsername = data.username;
          setCsrfToken(data.csrfToken || ""); window.__csrfToken = data.csrfToken || "";
          setMfaEnrolled(Boolean(data.mfaEnrolled));
          applyAuthSessionPayload(data);
          setStatus("mfa_required");
          return;
        }
      } catch (_) {
        // no-op
      }
      if (mounted) setStatus("unauthenticated");
    }
    checkSession();
    return () => {
      mounted = false;
    };
  }, [applyAuthSessionPayload]);

  useEffect(() => {
    if (status !== "unauthenticated" || !GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    const buttonHost = googleBtnRef.current;
    loadGoogleScript()
      .then(() => {
        if (cancelled || !buttonHost || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        buttonHost.innerHTML = "";
        window.google.accounts.id.renderButton(buttonHost, {
          theme: "filled_black",
          shape: "rectangular",
          size: "large",
          width: 392,
          text: "signin_with",
          locale: "en",
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Could not load Google Sign-In.");
      });
    return () => {
      cancelled = true;
      if (buttonHost) buttonHost.innerHTML = "";
    };
  }, [status, handleGoogleCredential]);

  const onLogout = useCallback(async () => {
    try {
      await fetch(apiUrl("/auth/logout"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: "{}",
      });
    } finally {
      setStatus("unauthenticated");
      setCurrentUser("");
      window.__currentUsername = "";
      setCsrfToken(""); window.__csrfToken = "";
      setMfaToken("");
      setMfaSetupSecret("");
      setMfaSetupQr("");
      setMfaEnrolled(false);
    }
  }, [csrfToken]);

  useEffect(() => {
    const handler = () => {
      onLogout();
    };
    window.addEventListener("jf:logout-request", handler);
    return () => window.removeEventListener("jf:logout-request", handler);
  }, [onLogout]);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    idleWarningOpenRef.current = idleWarningOpen;
  }, [idleWarningOpen]);

  const clearIdleTimers = useCallback(() => {
    if (idleWarnTimerRef.current) {
      clearTimeout(idleWarnTimerRef.current);
      idleWarnTimerRef.current = null;
    }
    if (idleCountdownTimerRef.current) {
      clearInterval(idleCountdownTimerRef.current);
      idleCountdownTimerRef.current = null;
    }
    idleCountdownDeadlineRef.current = null;
    idleWarnFireAtRef.current = null;
  }, []);

  const startCountdown = useCallback((remainingSec) => {
    if (idleCountdownTimerRef.current) {
      clearInterval(idleCountdownTimerRef.current);
      idleCountdownTimerRef.current = null;
    }
    const deadline = Date.now() + Math.max(0, remainingSec) * 1000;
    idleCountdownDeadlineRef.current = deadline;
    setIdleWarningOpen(true);

    const tick = () => {
      const activeDeadline = idleCountdownDeadlineRef.current || deadline;
      const nextRemainingSec = Math.max(0, Math.ceil((activeDeadline - Date.now()) / 1000));
      setIdleRemainingSec(nextRemainingSec);
      if (nextRemainingSec <= 0) {
        if (idleCountdownTimerRef.current) {
          clearInterval(idleCountdownTimerRef.current);
          idleCountdownTimerRef.current = null;
        }
        idleCountdownDeadlineRef.current = null;
        setIdleWarningOpen(false);
        const signOut = onLogoutRef.current;
        if (typeof signOut === "function") signOut();
      }
    };

    tick();
    if (!idleCountdownDeadlineRef.current) return;
    idleCountdownTimerRef.current = setInterval(() => {
      tick();
    }, 1000);
  }, []);

  const scheduleIdleWarning = useCallback(() => {
    clearIdleTimers();
    const warnAfter = Math.max(30_000, sessionIdleMs - sessionWarningLeadMs);
    idleWarnFireAtRef.current = Date.now() + warnAfter;
    idleWarnTimerRef.current = setTimeout(() => {
      const leadSec = Math.max(1, Math.floor(sessionWarningLeadMs / 1000));
      startCountdown(leadSec);
    }, warnAfter);
  }, [clearIdleTimers, startCountdown, sessionIdleMs, sessionWarningLeadMs]);

  const handleIdleContinue = useCallback(async () => {
    clearIdleTimers();
    setIdleWarningOpen(false);
    try {
      const res = await fetch(apiUrl("/auth/session"), { credentials: "include" });
      const data = await parseJsonFromApiResponse(res);
      if (!res.ok || data?.ok !== true || !data.authenticated || !data.username || !data.csrfToken) {
        throw new Error("Your session has expired. Please sign in again.");
      }
      setCurrentUser(data.username);
      window.__currentUsername = data.username;
      setCsrfToken(data.csrfToken);
      window.__csrfToken = data.csrfToken;
      setMfaEnrolled(Boolean(data.mfaEnrolled));
      applyAuthSessionPayload(data);
      scheduleIdleWarning();
    } catch (err) {
      setError(err?.message || "Your session has expired. Please sign in again.");
      await onLogout();
    }
  }, [clearIdleTimers, scheduleIdleWarning, applyAuthSessionPayload, onLogout]);

  const bumpActivity = useCallback(() => {
    if (idleWarningOpenRef.current) return;
    // If the warning time already elapsed (e.g. tab was hidden through it and
    // setTimeout was throttled), fire the warning/logout instead of resetting.
    // Otherwise mouse/click events on tab return would race ahead of
    // visibilitychange and silently reset the idle timer.
    if (idleWarnFireAtRef.current && Date.now() >= idleWarnFireAtRef.current) {
      const elapsedIntoCountdown = Date.now() - idleWarnFireAtRef.current;
      const leadMs = Math.max(0, sessionWarningLeadMs - elapsedIntoCountdown);
      const remainingSec = Math.max(0, Math.floor(leadMs / 1000));
      clearIdleTimers();
      if (remainingSec <= 0) {
        setIdleWarningOpen(false);
        const signOut = onLogoutRef.current;
        if (typeof signOut === "function") signOut();
      } else {
        startCountdown(remainingSec);
      }
      return;
    }
    scheduleIdleWarning();
  }, [scheduleIdleWarning, clearIdleTimers, startCountdown, sessionWarningLeadMs]);

  useEffect(() => {
    if (status !== "authenticated") {
      clearIdleTimers();
      setIdleWarningOpen(false);
      return undefined;
    }
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click", "wheel"];
    const onEvt = () => bumpActivity();
    events.forEach((e) => window.addEventListener(e, onEvt, { passive: true }));

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // If the session warning timer has already elapsed while we were away, fire it immediately.
      if (idleWarnFireAtRef.current && !idleCountdownTimerRef.current) {
        const now = Date.now();
        const msUntilWarn = idleWarnFireAtRef.current - now;
        if (msUntilWarn <= 0) {
          // Warning time already passed — compute how many seconds remain in the countdown window.
          const elapsedIntoCountdown = -msUntilWarn;
          const leadMs = Math.max(0, sessionWarningLeadMs - elapsedIntoCountdown);
          const remainingSec = Math.max(0, Math.floor(leadMs / 1000));
          if (remainingSec <= 0) {
            // Entire countdown also elapsed — sign out immediately.
            clearIdleTimers();
            setIdleWarningOpen(false);
            const signOut = onLogoutRef.current;
            if (typeof signOut === "function") signOut();
          } else {
            clearTimeout(idleWarnTimerRef.current);
            idleWarnTimerRef.current = null;
            idleWarnFireAtRef.current = null;
            startCountdown(remainingSec);
          }
        } else {
          // Warning hasn't fired yet; reschedule with the remaining time so the
          // timer is accurate after the tab was suspended.
          if (idleWarnTimerRef.current) clearTimeout(idleWarnTimerRef.current);
          idleWarnFireAtRef.current = now + msUntilWarn;
          idleWarnTimerRef.current = setTimeout(() => {
            const leadSec = Math.max(1, Math.floor(sessionWarningLeadMs / 1000));
            startCountdown(leadSec);
          }, msUntilWarn);
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    scheduleIdleWarning();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onEvt));
      document.removeEventListener("visibilitychange", onVisibility);
      clearIdleTimers();
    };
  }, [status, bumpActivity, scheduleIdleWarning, clearIdleTimers, startCountdown, sessionWarningLeadMs]);

  if (status === "loading") {
    return (
      <LoginBackdrop>
        <div style={{ ...LOGIN_CARD, maxWidth: 320, textAlign: "center", padding: "18px 20px" }}>
          <div style={LOGIN_CAPTION}>{copy.loadingCaption}</div>
          <p style={{ margin: "6px 0 0", color: L.mid, fontSize: 13 }}>Checking secure session…</p>
        </div>
      </LoginBackdrop>
    );
  }

  if (status !== "authenticated") {
    if (status === "mfa_required") {
      const startSetup = async () => {
        setError("");
        setMfaBusy(true);
        try {
          const res = await fetch(apiUrl("/auth/mfa/setup"), {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          });
          const data = await parseJsonFromApiResponse(res);
          if (!res.ok || !data.ok) throw new Error(data.error || "Could not start authenticator setup.");
          setMfaSetupSecret(data.secret || "");
          setMfaSetupQr(data.qrImageDataUrl || "");
        } catch (e) {
          setError(e.message || "Could not start authenticator setup.");
        } finally {
          setMfaBusy(false);
        }
      };

      const submitCode = async () => {
        const clean = (mfaToken || "").trim().replace(/\s+/g, "");
        if (!clean) {
          setError("Enter your 6-digit authenticator code.");
          return;
        }
        setError("");
        setMfaBusy(true);
        try {
          const endpoint = mfaEnrolled ? "/auth/mfa/verify" : "/auth/mfa/enable";
          const res = await fetch(apiUrl(endpoint), {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: clean }),
          });
          const data = await parseJsonFromApiResponse(res);
          if (!res.ok || !data.ok) throw new Error(data.error || "Authenticator verification failed.");
          const freshToken = data.csrfToken || csrfToken;
          setCsrfToken(freshToken); window.__csrfToken = freshToken;
          setMfaToken("");
          setMfaSetupSecret("");
          setMfaSetupQr("");
          setMfaEnrolled(true);
          applyAuthSessionPayload(data);
          setStatus("authenticated");
        } catch (e) {
          setError(e.message || "Authenticator verification failed.");
        } finally {
          setMfaBusy(false);
        }
      };

      return (
        <LoginBackdrop>
          <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
            <div style={{ width: 132, height: 132, display: "flex", alignItems: "center", justifyContent: "center", filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.45))" }}>
              <img src={PUBLIC_ASSET("CompanyBkgrnd.png")} alt="J & F Professional Solutions" style={{ width: 128, height: 128, objectFit: "contain" }} />
            </div>
            <div style={{ marginTop: 8, textAlign: "center", textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}>
              <div style={{ color: "#fff", fontSize: 22, fontWeight: 800, letterSpacing: -0.2, lineHeight: 1.15 }}>J &amp; F Professional Solutions</div>
              <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2, marginTop: 4 }}>{copy.subtitle}</div>
            </div>
          </div>
          <div style={{ ...LOGIN_CARD, maxWidth: 460 }}>
            <div style={LOGIN_CAPTION}>Secure sign-in · step 2 of 2</div>
            <h2 style={{ margin: "4px 0 4px", fontSize: 20, fontWeight: 800, color: L.text }}>Authenticator verification</h2>
            <p style={{ margin: "0 0 12px", color: L.mid, fontSize: 12.5, lineHeight: 1.45 }}>
              Signed in as <strong style={{ color: L.text }}>{currentUser}</strong>. Enter the code from your authenticator app to continue.
            </p>
            {!mfaEnrolled ? (
              <div style={{ marginBottom: 12, padding: "10px 12px", border: `1px solid ${L.border}`, borderRadius: 4, background: L.bg }}>
                <div style={{ ...LOGIN_CAPTION, marginBottom: 6 }}>First time here</div>
                <button type="button" onClick={startSetup} style={{ ...BTN_STYLE, background: L.card, color: L.navy }} disabled={mfaBusy}>
                  {mfaBusy ? "Preparing..." : "Set up authenticator app"}
                </button>
                {mfaSetupQr ? <div style={{ marginTop: 10, textAlign: "center" }}><img src={mfaSetupQr} alt="Authenticator QR code" style={{ width: 168, height: 168, border: `1px solid ${L.border}`, borderRadius: 4, background: "#fff" }} /></div> : null}
                {mfaSetupSecret ? (
                  <p style={{ color: L.mid, fontSize: 12, margin: "8px 0 0", overflowWrap: "anywhere" }}>
                    Manual setup key: <code style={{ fontSize: 12, color: L.text }}>{mfaSetupSecret}</code>
                  </p>
                ) : null}
              </div>
            ) : null}
            <label htmlFor="mfa-code" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={LOGIN_CAPTION}>6-digit code</span>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="text"
                  id="mfa-code"
                  name="mfa-code"
                  value={mfaToken}
                  onChange={(e) => setMfaToken(e.target.value)}
                  placeholder="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  style={{ ...LOGIN_INPUT, flex: 1, fontFamily: "monospace", fontSize: 16, letterSpacing: 2 }}
                />
                <button type="button" onClick={submitCode} style={{ ...BTN_STYLE, width: "auto" }} disabled={mfaBusy}>
                  {mfaBusy ? "Verifying..." : "Verify"}
                </button>
              </div>
            </label>
            {error ? <p style={{ color: L.danger, margin: "10px 0 0", fontSize: 12.5 }}>{error}</p> : null}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${L.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: L.light }}>Authorized users only · activity is logged</span>
              <button type="button" onClick={onLogout} style={{ ...BTN_STYLE, width: "auto", background: L.card, color: L.danger, border: `1px solid ${L.danger}` }}>
                Sign out
              </button>
            </div>
          </div>
        </LoginBackdrop>
      );
    }
    return (
      <LoginBackdrop>
        {/* Company logo — centered above the card (client 2026-09-05) */}
        <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
          <div style={{ width: 132, height: 132, display: "flex", alignItems: "center", justifyContent: "center", filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.45))" }}>
            <img src={PUBLIC_ASSET("CompanyBkgrnd.png")} alt="J & F Professional Solutions" style={{ width: 128, height: 128, objectFit: "contain" }} />
          </div>
          <div style={{ marginTop: 8, textAlign: "center", textShadow: "0 2px 10px rgba(0,0,0,0.6)" }}>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 800, letterSpacing: -0.2, lineHeight: 1.15 }}>J &amp; F Professional Solutions</div>
            <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.2, marginTop: 4 }}>{copy.subtitle}</div>
          </div>
        </div>

        {/* Sign-in card — square, compact, the app's own look (2026-09-05). Only the styling changed:
            the PHI acknowledgment, its gate on the Google button, the button host and every link are as before. */}
        <div style={LOGIN_CARD}>
          <div style={LOGIN_CAPTION}>{copy.caption}</div>
          <h1 style={{ margin: "4px 0 2px", fontSize: 22, fontWeight: 800, color: L.text, letterSpacing: -0.2 }}>Sign in</h1>
          <p style={{ margin: "0 0 12px", color: L.mid, fontSize: 12.5, lineHeight: 1.45 }}>
            {copy.blurb}
          </p>

          {/* Google sign-in — first thing on the card; still inactive until the acknowledgment below is ticked */}
          {!GOOGLE_CLIENT_ID ? (
            <p style={{ color: L.danger, fontSize: 12.5 }}>
              Missing <code>REACT_APP_GOOGLE_CLIENT_ID</code> — check <code>.env.production</code>.
            </p>
          ) : (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "stretch",
              padding: "2px 0 4px",
              opacity: phiAcknowledged ? 1 : 0.38,
              pointerEvents: phiAcknowledged ? "auto" : "none",
              transition: "opacity 0.2s",
            }}>
              <div style={{ minHeight: 44 }} ref={googleBtnRef} />
            </div>
          )}
          {!phiAcknowledged && GOOGLE_CLIENT_ID ? (
            <div style={{ margin: "4px 0 0", fontSize: 11, color: L.light, textAlign: "center" }}>Tick the acknowledgment below to enable sign-in</div>
          ) : null}

          {error ? <p style={{ color: L.danger, margin: "10px 0 0", fontSize: 12.5 }}>{error}</p> : null}

          {/* PHI acknowledgment — must be ticked before sign-in is active */}
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            margin: "14px 0 0", padding: "10px 12px",
            background: L.amberBg, borderRadius: 4,
            border: `1px solid ${L.amberBorder}`, textAlign: "left"
          }}>
            <input
              type="checkbox"
              id="phi-ack"
              checked={phiAcknowledged}
              onChange={e => setPhiAcknowledged(e.target.checked)}
              style={{ marginTop: 2, cursor: "pointer", flexShrink: 0, accentColor: L.navy }}
            />
            <label htmlFor="phi-ack" style={{ fontSize: 12, color: L.amberText, cursor: "pointer", lineHeight: 1.5 }}>
              This portal holds <strong>Protected Health Information (PHI)</strong>. I confirm I am an authorized user, will open only the records my work requires, and will handle them under HIPAA and the J&amp;F Professional Solutions{" "}
              <a
                href="/acceptable-use.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: L.navy, textDecoration: "underline", fontWeight: 700 }}
              >
                Acceptable Use Policy
              </a>
              . Activity is logged.
            </label>
          </div>

          {/* Card footer */}
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${L.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <img src={PUBLIC_ASSET("CompanyBkgrnd.png")} alt="" aria-hidden="true" style={{ width: 18, height: 18, objectFit: "contain" }} />
              <span style={{ color: L.mid, fontSize: 11, fontWeight: 700 }}>Secure healthcare billing &amp; reconciliation</span>
            </div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <span style={{ color: L.light, fontSize: 11, fontWeight: 700 }}>Authorized users only</span>
              <a href={copy.crossHref} style={{ color: L.navy, fontSize: 11, fontWeight: 800, textDecoration: "none", whiteSpace: "nowrap" }} title={variant === "portal" ? "J & F staff sign-in" : "Client portal sign-in"}>{copy.crossLabel}</a>
            </span>
          </div>
        </div>

        {/* Contact support — bottom right */}
        <div style={{ position: "absolute", bottom: 18, right: 20, zIndex: 10, display: "inline-flex", alignItems: "center", gap: 8, height: 28, padding: "0 10px", borderRadius: 4, background: "rgba(255,255,255,0.92)", border: `1px solid ${L.border}`, color: L.mid, fontSize: 12, fontWeight: 700 }}>
          <span style={{ width: 16, height: 16, borderRadius: 4, border: `1px solid ${L.navy}`, display: "inline-flex", alignItems: "center", justifyContent: "center", color: L.navy, fontSize: 10, fontWeight: 900 }}>?</span>
          Contact support for account access
        </div>
      </LoginBackdrop>
    );
  }

  const idleFmt = `${Math.floor(idleRemainingSec / 60)}:${String(idleRemainingSec % 60).padStart(2, "0")}`;

  return (
    <>
      {idleWarningOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            fontFamily: "Inter, system-ui, sans-serif",
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="idle-warn-title"
            style={{
              maxWidth: 440,
              width: "100%",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              padding: "28px 24px",
              border: "1px solid #e2e8f0",
            }}
          >
            <h2 id="idle-warn-title" style={{ margin: "0 0 10px", fontSize: 20, color: "#0f172a" }}>
              Session timeout warning
            </h2>
            <p style={{ margin: "0 0 8px", color: "#475569", fontSize: 14, lineHeight: 1.5 }}>
              For your security, you will be signed out soon due to inactivity.
            </p>
            <p style={{ margin: "0 0 20px", color: "#0f172a", fontSize: 15, fontWeight: 600 }}>
              Time remaining:{" "}
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{idleFmt}</span>
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={handleIdleContinue} style={{ ...BTN_STYLE, flex: "1 1 140px", margin: 0 }}>
                Continue session
              </button>
              <button
                type="button"
                onClick={() => {
                  clearIdleTimers();
                  setIdleWarningOpen(false);
                  onLogout();
                }}
                style={{ ...BTN_STYLE, flex: "1 1 140px", margin: 0, background: "#64748b" }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {children}
    </>
  );
}
